import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Inbox, Hospital, Search, Clock, Filter, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ChatTicket, fetchPendingTickets, resolveTicket } from "@/services/chat-service";
import { fetchClinicById, Clinic } from "@/services/clinic-service";
import { ChatObserverTable } from "@/components/chat/ChatObserverTable";
import { ChatHistoryPanel } from "@/components/chat/ChatHistoryPanel";
import { InviteAutomationStandaloneDialog } from "@/components/chat/InviteAutomationStandaloneDialog";
import { useClinic } from "@/contexts/ClinicContext";
import { cn } from "@/lib/utils";

const POLL_BASE = 15_000;
const POLL_MAX  = 120_000;

function formatAgo(date: Date | null): string {
  if (!date) return "";
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 10) return "agora";
  if (secs < 60) return `${secs}s atrás`;
  return `${Math.floor(secs / 60)}min atrás`;
}

export default function ChatObserver() {
  const { selectedClinicId, selectedClinic } = useClinic();
  const [tickets, setTickets]               = useState<ChatTicket[]>([]);
  const [loading, setLoading]               = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ChatTicket | null>(null);
  const [search, setSearch]                 = useState("");
  const [onlyWaiting, setOnlyWaiting]       = useState(false);
  const [lastSync, setLastSync]             = useState<Date | null>(null);
  const [, tick]                            = useState(0); // força re-render para "ago"
  const [fullClinic, setFullClinic]         = useState<Clinic | null>(null);
  const [inviteOpen, setInviteOpen]         = useState(false);
  const { toast } = useToast();

  // Ref para acessar selectedTicket sem stale closure no polling
  const selectedTicketRef = useRef(selectedTicket);
  useEffect(() => { selectedTicketRef.current = selectedTicket; }, [selectedTicket]);

  // Carrega clinic completa pra acessar campos do provider (twilio_*).
  useEffect(() => {
    if (!selectedClinicId) { setFullClinic(null); return; }
    fetchClinicById(selectedClinicId)
      .then(setFullClinic)
      .catch(() => setFullClinic(null));
  }, [selectedClinicId]);

  // --- Fetch central (usado pelo polling e pelo botão manual) ---
  const fetchData = useCallback(async (): Promise<boolean> => {
    if (!selectedClinicId) return false;
    setLoading(true);
    try {
      const data = await fetchPendingTickets(selectedClinicId);
      setTickets(data);
      const cur = selectedTicketRef.current;
      if (cur) {
        const fresh = data.find(t => t.id === cur.id);
        if (fresh) setSelectedTicket(fresh);
      }
      setLastSync(new Date());
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedClinicId]);

  // --- Botão manual de refresh ---
  const handleRefresh = useCallback(async () => {
    const ok = await fetchData();
    if (!ok) toast({ title: "Erro ao atualizar", description: "Não foi possível sincronizar com o WhatsApp.", variant: "destructive" });
  }, [fetchData, toast]);

  // --- Polling com backoff exponencial ---
  useEffect(() => {
    if (!selectedClinicId) {
      setTickets([]);
      setSelectedTicket(null);
      setLastSync(null);
      return;
    }

    let delay      = POLL_BASE;
    let errorStreak = 0;
    let timerId: ReturnType<typeof setTimeout>;
    let cancelled  = false;

    const poll = async () => {
      if (cancelled) return;
      const ok = await fetchData();
      if (!ok) {
        errorStreak++;
        delay = Math.min(POLL_BASE * Math.pow(2, errorStreak - 1), POLL_MAX);
        if (errorStreak === 1) {
          toast({ title: "Erro ao sincronizar mensagens", description: "Tentando novamente em breve.", variant: "destructive" });
        }
      } else {
        errorStreak = 0;
        delay = POLL_BASE;
      }
      if (!cancelled) timerId = setTimeout(poll, delay);
    };

    poll();
    return () => { cancelled = true; clearTimeout(timerId); };
  }, [selectedClinicId, fetchData, toast]);

  // --- Ticker: atualiza "syncAgo" a cada 5s ---
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // --- Resolver conversa (optimistic) ---
  const handleResolve = useCallback(async (ticket: ChatTicket) => {
    setTickets(prev => prev.filter(t => t.id !== ticket.id));
    if (selectedTicketRef.current?.id === ticket.id) setSelectedTicket(null);
    try {
      await resolveTicket(ticket.id);
      toast({ title: "Conversa arquivada!" });
    } catch {
      setTickets(prev => [...prev, ticket]);
      toast({ title: "Erro ao arquivar", variant: "destructive" });
    }
  }, [toast]);

  // --- Filtros ---
  const filteredTickets = tickets.filter(t => {
    if (onlyWaiting && t.last_message_direction !== "IN") return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.patient_name || "").toLowerCase().includes(q) ||
      (t.cellphone || "").includes(q)
    );
  });

  const pendingCount = tickets.filter(t => t.last_message_direction === "IN").length;

  // --- Atalhos de teclado: ↑/↓ navegar · e resolver · Esc desselecionar ---
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

      if (e.key === "Escape") { setSelectedTicket(null); return; }

      if (e.key === "e" && selectedTicketRef.current) {
        e.preventDefault();
        handleResolve(selectedTicketRef.current);
        return;
      }

      if (["ArrowDown", "ArrowUp", "j", "k"].includes(e.key)) {
        if (filteredTickets.length === 0) return;
        e.preventDefault();
        const cur = selectedTicketRef.current;
        const currentIdx = cur ? filteredTickets.findIndex(t => t.id === cur.id) : -1;
        const dir = (e.key === "ArrowDown" || e.key === "j") ? 1 : -1;
        const nextIdx = currentIdx === -1
          ? (dir === 1 ? 0 : filteredTickets.length - 1)
          : (currentIdx + dir + filteredTickets.length) % filteredTickets.length;
        setSelectedTicket(filteredTickets[nextIdx]);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [filteredTickets, handleResolve]);

  if (!selectedClinicId) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <p className="font-display italic text-muted-foreground text-lg">
          Selecione uma clínica no topo da tela para visualizar as mensagens
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex overflow-hidden">
      {/* Coluna esquerda — Inbox */}
      <aside className="w-[440px] border-r border-border flex flex-col bg-card">

        {/* Header */}
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <Inbox className="h-4 w-4 text-primary" />
            <h1 className="font-display text-base font-medium">Caixa de Mensagens</h1>

            {/* Última sincronização */}
            {lastSync && (
              <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground font-display italic">
                <Clock className="h-2.5 w-2.5" />
                {formatAgo(lastSync)}
              </span>
            )}

            <Button
              size="icon"
              variant="ghost"
              onClick={handleRefresh}
              disabled={loading}
              className={cn("h-6 w-6", lastSync ? "ml-1" : "ml-auto")}
              title="Atualizar"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {selectedClinic && (
            <div className="flex items-center gap-1.5 mb-2">
              <Hospital className="h-3 w-3 text-accent" />
              <span className="text-[11px] font-display italic text-accent">
                {selectedClinic.trade_name}
              </span>
            </div>
          )}

          {/* Botão Convidar pro Automatizado */}
          {fullClinic?.twilio_whatsapp_from && (
            <Button
              variant="outline"
              size="sm"
              className="w-full mb-2 h-7 text-[11px] gap-1.5"
              onClick={() => setInviteOpen(true)}
            >
              <Smartphone className="h-3 w-3" />
              Convidar pro Atendimento Automatizado
            </Button>
          )}

          {/* Contagens + toggle "só aguardando" */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <span className="tabular-nums">{tickets.length}</span>
            <span>conversas</span>
            <span className="text-border">·</span>
            <span className={pendingCount > 0 ? "text-accent font-semibold tabular-nums" : "tabular-nums"}>
              {pendingCount}
            </span>
            <span className={pendingCount > 0 ? "text-accent" : ""}>aguardando</span>

            <button
              type="button"
              onClick={() => setOnlyWaiting(v => !v)}
              className={cn(
                "ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all",
                onlyWaiting
                  ? "bg-accent/10 text-accent border-accent/30"
                  : "bg-transparent text-muted-foreground border-border hover:border-accent/30 hover:text-accent"
              )}
              title="Mostrar só as que aguardam resposta"
            >
              <Filter className="h-2.5 w-2.5" />
              Só aguardando
            </button>
          </div>

          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              className="h-8 pl-8 text-xs bg-background"
            />
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto">
          <ChatObserverTable
            tickets={filteredTickets}
            loading={loading}
            onResolve={handleResolve}
            onViewHistory={setSelectedTicket}
            selectedId={selectedTicket?.id}
          />
        </div>
      </aside>

      {/* Coluna direita — Histórico */}
      <main className="flex-1 min-w-0">
        <ChatHistoryPanel
          ticketId={selectedTicket?.id}
          cellphone={selectedTicket?.cellphone}
          patientName={selectedTicket?.patient_name}
          clinicTradeName={selectedClinic?.trade_name}
          clinicId={selectedClinicId}
        />
      </main>

      {fullClinic?.twilio_whatsapp_from && selectedClinicId && (
        <InviteAutomationStandaloneDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          clinicId={selectedClinicId}
          twilioWhatsappFrom={fullClinic.twilio_whatsapp_from}
          clinicTradeName={selectedClinic?.trade_name}
        />
      )}
    </div>
  );
}
